import { useCallback, useEffect, useState } from 'react';
import ApiDatabase from 'server';

// The route's own key: closing the panel cancels this read and nothing else.
export const APPENDICES_ABORT_KEY = 'admin/invoices/:number/appendices';

/**
 * The appendices of an invoice (JBWEB-1277), oldest first, read when the
 * section shows. `items` is null until read; `failed` says the read failed.
 * `add`, `replace` and `remove` apply what a write answered, without a new read.
 */
const useInvoiceAppendices = (number) => {
  const [state, setState] = useState({
    items: null,
    isLoading: true,
    failed: false,
  });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!number) return undefined;
    let active = true;
    setState((previous) => ({ ...previous, isLoading: true, failed: false }));
    ApiDatabase.getInvoiceAppendices(
      { number },
      (data) => {
        if (!active) return;
        setState({
          items: Array.isArray(data?.items) ? data.items : [],
          isLoading: false,
          failed: false,
        });
      },
      (error) => {
        if (!active || error?.name === 'CanceledError') return;
        // Never the error object in the console: axios keeps the request, token included.
        setState((previous) => ({
          ...previous,
          isLoading: false,
          failed: true,
        }));
      },
      { abortKey: APPENDICES_ABORT_KEY },
    );
    return () => {
      active = false;
    };
  }, [number, reloadKey]);

  useEffect(() => () => ApiDatabase.abortRequest(APPENDICES_ABORT_KEY), []);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const add = useCallback(
    (appendix) =>
      setState((previous) => ({
        ...previous,
        items: [...(previous.items ?? []), appendix],
      })),
    [],
  );
  const replace = useCallback(
    (appendix) =>
      setState((previous) => ({
        ...previous,
        items: (previous.items ?? []).map((item) =>
          item.id === appendix.id ? appendix : item,
        ),
      })),
    [],
  );
  const remove = useCallback(
    (appendixId) =>
      setState((previous) => ({
        ...previous,
        items: (previous.items ?? []).filter((item) => item.id !== appendixId),
      })),
    [],
  );
  return { ...state, reload, add, replace, remove };
};

export default useInvoiceAppendices;
